import type { TypedWrappedFieldProps } from "redux-form";
import cx from "classnames";

import type {
  ButtonVariants,
  EnhanceElementProps,
} from "@braintrust/braintrust-ui-components";
import {
  Box,
  Button,
  IconButton,
  MenuSelect,
  Stack,
  Tooltip,
  Typography,
  useButtonSelect,
  useMediaQuery,
} from "@braintrust/braintrust-ui-components";
import {
  InfoIcon,
  MenuArrowDownIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useIsNodeStaff, useUser } from "@js/apps/common/hooks";
import { useIsNodeOrImpersonated } from "@js/apps/common/hooks/use-is-node-or-impersonated";
import { useGetManagedEmployerQuery } from "@js/apps/employer/hooks";
import { getJobTypeLabels } from "@js/apps/jobs/utils";
import { RouterLink } from "@js/components/link";
import { violetMenuItemSx } from "@js/components/violet-menu-item";
import type { JobSubType } from "@js/types/jobs";
import { enumToOptions } from "@js/utils";

import styles from "./style.module.scss";

const optionsVariants: Record<JobType, ButtonVariants> = {
  grant: "gray-red",
  direct_hire: "gray-orange",
  freelance: "gray-yellow",
};

type JobType = EnumType<typeof ENUMS.JobType>;

type JobTypeFieldProps = {
  job_type: TypedWrappedFieldProps<JobType | undefined>;
  sub_type: TypedWrappedFieldProps<JobSubType | undefined>;
  talentHiredCount?: number;
};

export const JobTypeField = ({
  job_type,
  sub_type,
  talentHiredCount,
}: JobTypeFieldProps) => {
  const isMobile = useMediaQuery("sm");
  const { handleChange, isSelected, containerA11yProps, itemA11yProps } =
    useButtonSelect({
      onChange: (value) => {
        job_type.input.onChange(value as JobType);
        if (value !== ENUMS.JobType.FREELANCE) {
          sub_type.input.onChange(null);
        }
      },
      value: job_type.input.value,
    });

  const isNodeStaffOrImpersonated = useIsNodeOrImpersonated();
  const isStaff = useIsNodeStaff();

  const { data: employerProfile } = useGetManagedEmployerQuery();
  const { can_create_grant_job } = employerProfile || {};

  const canCreateGrantJob = can_create_grant_job || isStaff;

  const user = useUser();
  const JobTypeLabels = getJobTypeLabels(user?.account_type);

  const options = canCreateGrantJob
    ? enumToOptions(JobTypeLabels)
    : enumToOptions(JobTypeLabels).filter(
        (option) => option.value !== ENUMS.JobType.GRANT,
      );

  const isFreelancerTypeSelected =
    job_type.input.value === ENUMS.JobType.FREELANCE;

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography component="p" variant="label" sx={{ display: "block" }}>
          What type of job is this?
        </Typography>
        <Tooltip
          title={
            <Typography component="p" variant="paragraph">
              The main difference between these is the contract type and how
              Braintrust fees are handled.{" "}
              <Typography
                variant="link"
                component="a"
                href={SETTINGS.SUPPORT_URLS.JOB_TYPES_AND_FEES_ARTICLE_URL}
                rel="noopener noreferrer"
                target="_blank"
                color="inherit"
                RouterLink={RouterLink}
              >
                Learn more about job types and fees ↗️
              </Typography>
            </Typography>
          }
          placement={isMobile ? "top" : "right"}
        >
          <span>
            <IconButton variant="transparent" size="small" aria-label="Info">
              <InfoIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {job_type.meta.error && job_type.meta.touched && (
        <Typography component="p" color="negative" size="small">
          {job_type.meta.error}
        </Typography>
      )}

      <Tooltip
        title="You can't change job's type after hiring a Talent."
        disabled={!talentHiredCount}
        placement="top"
        enterNextDelay={500}
      >
        <Box className={styles.jobTypeField} {...containerA11yProps}>
          {options.map((option) => {
            const _isSelected = isSelected(option);

            return (
              <Button
                key={option.value}
                {...itemA11yProps(_isSelected)}
                variant={optionsVariants[option.value]}
                shape="squared"
                className={cx(styles.jobTypeFieldOption, {
                  selected: _isSelected,
                })}
                onClick={() => {
                  handleChange(option);
                }}
                disabled={!!talentHiredCount}
              >
                <Typography variant="label" size="large" component="span">
                  {option.label}
                </Typography>
              </Button>
            );
          })}
        </Box>
      </Tooltip>

      {isNodeStaffOrImpersonated && isFreelancerTypeSelected && (
        <Stack
          direction="row"
          sx={{ alignItems: "center", gap: 1, mt: 2, mb: -1 }}
        >
          <Typography component="span" variant="paragraph">
            Job sub type:
          </Typography>
          <MenuSelect
            value={sub_type.input.value}
            onChange={(value) => {
              const valueOrNull = (value || null) as JobSubType | null;

              sub_type.input.onChange(valueOrNull);
            }}
            menuItemSx={violetMenuItemSx}
            disableMenuItemRipple
            type="radio"
            anchor={(props) =>
              renderSubTypeAnchor({
                props,
                subType: sub_type.input.value,
              })
            }
            sx={{ li: { padding: "10px 12px !important" } }}
            options={subTypeOptions}
          />
        </Stack>
      )}
    </>
  );
};

const noneOption = { label: "None", value: null } as const;
const subTypeOptions = [
  noneOption,
  ...Object.values(ENUMS.JobSubType).map((subType) => {
    return { label: ENUMS.JobSubTypeLabels[subType], value: subType };
  }),
];

type AnchorProps = {
  anchorProps: EnhanceElementProps;
  isOpen: boolean;
};

const renderSubTypeAnchor = ({
  props,
  subType,
}: {
  props: AnchorProps;
  subType: JobSubType | undefined;
}) => {
  const { anchorProps, isOpen } = props;
  const {
    "aria-controls": ariaControls,
    "aria-haspopup": ariaHasPopup,
    ...rest
  } = anchorProps;

  const selectedOption =
    subTypeOptions.find((option) => option.value === subType) ?? noneOption;

  return (
    <Button
      aria-controls={ariaControls}
      aria-haspopup={ariaHasPopup}
      style={{ padding: "6px 8px" }}
      shape="squared"
      variant="transparent-with-text-hover"
      aria-label="Select job sub type"
      {...rest}
    >
      {selectedOption?.label}
      <Box sx={{ ml: 1, pt: 0.5 }}>
        <MenuArrowDownIcon
          sx={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </Box>
    </Button>
  );
};
